import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FlattenedUser } from '../../types/user';
import { useSelector } from 'react-redux';
import { gql } from '@apollo/client';
import styles from './SubscriptionTrialEnding.module.scss';
import { Alert, Collapse, IconButton, Typography, Link } from '@mui/material';
import { Icon } from '../icons';
import {
	EventCategory,
	UserType,
	useSubscriptionTrialEnding_EventCreatedSubscription,
	useSubscriptionTrialEnding_GetEventsWhereQuery,
	useSubscriptionTrialEnding_DismissEventMutation,
	useSubscriptionTrialEnding_GetOrganizationWhereQuery,
	SubscriptionStatus,
} from '#types';
import { useToast } from '../../hooks/useToast';
import { CloseIcon } from '../../mui/Icons';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const SubscriptionTrialEnding_GetOrganizationWhere = gql`
	query SubscriptionTrialEnding_GetOrganizationWhere(
		$where: OrganizationWhereUniqueInput!
	) {
		getOrganizationWhere(where: $where) {
			id
			subscription {
				id
				status
				subscriptionPlan {
					id
					type
					period
				}
			}
		}
	}
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const SubscriptionTrialEnding_GetEventsWhere = gql`
	query SubscriptionTrialEnding_GetEventsWhere($where: EventWhereInput!) {
		getEventsWhere(where: $where) {
			id
			dismissingUsers {
				id
			}
		}
	}
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const SubscriptionTrialEnding_DismissEvent = gql`
	mutation SubscriptionTrialEnding_DismissEvent(
		$where: EventWhereUniqueInput!
	) {
		dismissEvent(where: $where)
	}
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const SubscriptionTrialEnding_EventCreated = gql`
	subscription SubscriptionTrialEnding_EventCreated {
		eventCreated {
			id
			category
		}
	}
`;

export const SubscriptionTrialEnding: React.FC = () => {
	const user: FlattenedUser = useSelector( ( state: any ) => state.user );
	const { showError } = useToast();
	const [ open, setOpen ] = useState( false );

	const orgUser =
		user?.isLoggedIn && user.userType === UserType.OrgUser ? user : undefined;

	/* using `orgUser?.organization` here to filter out when an orgUser is
	onboarding, before they get an organization created for them */
	const skip = orgUser?.organization && orgUser.isAdmin ? false : true;

	const { data: organizationData, error: organizationError } =
		useSubscriptionTrialEnding_GetOrganizationWhereQuery( {
			variables: {
				where: { id: orgUser?.organization?.id || '' },
			},
			skip,
		} );

	const hasActiveOrTrialingSubscription =
		( organizationData?.getOrganizationWhere?.subscription?.status &&
			[ SubscriptionStatus.Active, SubscriptionStatus.Trialing ].includes(
				organizationData.getOrganizationWhere.subscription.status
			) ) ||
		false;
	const { period, type } =
		organizationData?.getOrganizationWhere?.subscription?.subscriptionPlan ||
		{};

	useEffect( () => {
		if ( organizationError ) {
			showError( organizationError );
		}
	}, [ organizationError, showError ] );

	const {
		data: events,
		error: eventsError,
		loading: eventsLoading,
	} = useSubscriptionTrialEnding_GetEventsWhereQuery( {
		variables: {
			where: {
				category: { in: [ EventCategory.SubscriptionTrialEnding ] },
				consumingUsers: {
					some: {
						id: orgUser?.user.id,
					},
				},
			},
		},
		skip,
	} );

	useEffect( () => {
		if ( eventsError ) {
			showError( eventsError );
		}
	}, [ eventsError, showError ] );

	const { data: subscription, error: subscriptionError } =
		useSubscriptionTrialEnding_EventCreatedSubscription( {
			skip,
		} );

	useEffect( () => {
		if ( subscriptionError ) {
			showError( subscriptionError );
		}
	}, [ subscriptionError, showError ] );

	const event = useMemo(
		() =>
			events?.getEventsWhere && events.getEventsWhere.length > 0
				? events.getEventsWhere[ 0 ]
				: subscription?.eventCreated.category ===
				  EventCategory.SubscriptionTrialEnding
					? subscription.eventCreated
					: undefined,
		[ events?.getEventsWhere, subscription?.eventCreated ]
	);

	const eventExistsButIsDismissed = useMemo(
		() =>
			( orgUser &&
				event &&
				'dismissingUsers' in event &&
				event.dismissingUsers?.some( ( u ) => u.id === orgUser.user.id ) ) ||
			false,
		[ orgUser, event ]
	);

	const [ dismissEvent, { loading: dismissing } ] =
		useSubscriptionTrialEnding_DismissEventMutation();

	useEffect( () => {
		setOpen( ( !!event && !eventExistsButIsDismissed ) || false );
	}, [ event, eventExistsButIsDismissed ] );

	const handleDismiss = useCallback( async () => {
		if ( event && orgUser ) {
			const { data, errors } = await dismissEvent( {
				variables: {
					where: {
						id: event.id,
					},
				},
			} );
			if ( errors ) {
				errors.forEach( ( error ) => showError( error ) );
			} else if ( !data?.dismissEvent ) {
				showError();
			} else {
				setOpen( false );
			}
		}
	}, [
		event,
		dismissEvent,
		orgUser,
		showError
	] );

	if ( eventsLoading ) {
		return null;
	}

	return (
		<Collapse in={ open }>
			<Alert
				severity='info'
				icon={ <Icon type='credit-card' className={ styles.icon } /> }
				variant='outlined'
				action={
					<IconButton
						aria-label='close'
						color='inherit'
						size='small'
						onClick={ handleDismiss }
						loading={ dismissing }
					>
						<CloseIcon fontSize='inherit' />
					</IconButton>
				}
			>
				<Typography>
					Your subscription trial is ending.{ ' ' }
					{ hasActiveOrTrialingSubscription ? (
						<>
							Add a payment method in your <Link href='/wallet'>Wallet</Link> to{ ' ' }
							continue with your currently selected { period } { type }{ ' ' }
							subscription.
						</>
					) : (
						<>
							Purchase a{ ' ' }
							<Link href='/admin?tab=subscription'>Subscription</Link> to keep{ ' ' }
							access to functionality like contracts, proposals, and leads!
						</>
					) }
				</Typography>
			</Alert>
		</Collapse>
	);
};
